import Footer from "components/footer/Footer";
import Sidebar from "components/sidebar";
import { Box } from "components/ui";
import { SuggestionBanner } from "components/suggestions/SuggestionBanner";
import {
  SuggestionsProvider,
  useSuggestions,
} from "contexts/SuggestionsContext";
import { SUGGESTION_BANNER_KEYS } from "services/suggestionBannerService";
import { OnboardingProvider } from "contexts/OnboardingContext";
import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import routes from "routes";
import { authService } from "services/authService";
import serverGenerationHubService from "services/serverGenerationHubService";
import { MdShield, MdBolt } from "react-icons/md";
import { getThemeColor } from "theme/colors";
import { useWidgetInit } from "hooks/useWidgetInit";
import { OnboardingModal } from "views/admin/onboarding/OnboardingModal";

const SUGGESTIONS_PATH = "/admin/suggestions";

export default function Admin() {
  return (
    <SuggestionsProvider>
      <AdminInner />
    </SuggestionsProvider>
  );
}

function AdminInner() {
  useWidgetInit();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(() => window.innerWidth >= 768);
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  // Dismissal is persisted per user on the server, so a closed banner stays closed across logins —
  // and comes back only when new suggestions of that kind appear.
  const {
    refreshCounts,
    maskingPendingCount,
    shortcutPendingCount,
    maskingBannerDismissed,
    shortcutBannerDismissed,
    initialized: suggestionsInitialized,
    dismissBanner,
  } = useSuggestions();
  const location = useLocation();
  const isOnMaskingPage = location.pathname.includes("security/data-privacy");
  const isOnSuggestionsPage = location.pathname.includes("suggestions");

  React.useEffect(() => {
    // Check if onboarding is needed
    const user = authService.getCurrentUser();
    const needsOnboarding = user?.company?.onboardingCompleted === false;
    setShowOnboarding(needsOnboarding);
  }, []);

  React.useEffect(() => {
    const handleResize = () => setOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Global listener: show masking suggestion toast whenever ANY toolkit generation completes with suggestions
  React.useEffect(() => {
    const handleGlobalProgress = (update: any) => {
      if (
        update.progress === 100 &&
        (update.suggestedMaskingRulesCount ?? 0) > 0
      ) {
        // The generator clears the dismissal server-side when it writes new suggestions, so all this
        // needs to do is pick up the fresh counts and flags.
        refreshCounts();
      }
    };

    serverGenerationHubService.onGlobalProgress(handleGlobalProgress);
    return () =>
      serverGenerationHubService.offGlobalProgress(handleGlobalProgress);
  }, [refreshCounts]);
  const getRoutes = (routes: RoutesType[]): any => {
    const features = authService.getCurrentUser()?.company?.features ?? [];

    return routes.flatMap((prop, key) => {
      const routeElements = [];

      // Handle regular routes with components
      if (prop.layout === "/admin" && prop.component) {
        const element =
          prop.requiredFeature && !features.includes(prop.requiredFeature) ? (
            <Navigate to="/admin/dashboard" replace />
          ) : (
            prop.component
          );
        routeElements.push(
          <Route path={`/${prop.path}`} element={element} key={key} />,
        );
      }

      // Handle sections with children
      if (prop.children) {
        prop.children.forEach((child, childKey) => {
          if (child.layout === "/admin" && child.component) {
            const element =
              child.requiredFeature &&
              !features.includes(child.requiredFeature) ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                child.component
              );
            routeElements.push(
              <Route
                path={`/${child.path}`}
                element={element}
                key={`${key}-${childKey}`}
              />,
            );
          }
        });
      }

      return routeElements.length > 0 ? routeElements : null;
    });
  };

  document.documentElement.dir = "ltr";
  return (
    <OnboardingProvider>
      <Box themeAware={false}>
        {/* Onboarding Modal - Non-dismissible */}
        {showOnboarding && (
          <OnboardingModal
            isOpen={showOnboarding}
            onClose={() => {
              // Reload to update user object with completed onboarding status
              window.location.reload();
            }}
          />
        )}

        <Box
          display="flex"
          minH="100vh"
          w="full"
          themeAware={false}
          overflowX="hidden"
        >
          <Sidebar
            open={open}
            onClose={() => setOpen(false)}
            onToggle={() => setOpen(!open)}
          />

          {/* Main Content - uses margin to account for sidebar width at xl breakpoint */}
          <Box
            display="flex"
            minH="100vh"
            w="full"
            flexDirection="column"
            bg={getThemeColor("bg.primary")}
            transition="all 0.3s"
            ml={{ base: "64px", md: open ? "240px" : "64px" }}
            themeAware={false}
            overflowX="hidden"
            minW={0}
          >
            {/* Persistent suggestion banners — re-appear on new suggestions, closable for the session */}
            {suggestionsInitialized &&
              maskingPendingCount > 0 &&
              !maskingBannerDismissed &&
              !isOnMaskingPage &&
              !isOnSuggestionsPage && (
                <SuggestionBanner
                  icon={<MdShield size={18} color="white" />}
                  message={t("app.suggestions.banner.masking", {
                    count: maskingPendingCount,
                  })}
                  linkLabel={t("app.suggestions.banner.reviewCta")}
                  linkTo="/admin/security/data-privacy"
                  bg="orange.300"
                  borderColor="orange.200"
                  dismissLabel={t("app.common.dismiss")}
                  onDismiss={() =>
                    dismissBanner(SUGGESTION_BANNER_KEYS.masking)
                  }
                />
              )}

            {suggestionsInitialized &&
              shortcutPendingCount > 0 &&
              !shortcutBannerDismissed &&
              !isOnSuggestionsPage && (
                <SuggestionBanner
                  icon={<MdBolt size={18} color="white" />}
                  message={t("app.suggestions.banner.shortcuts", {
                    count: shortcutPendingCount,
                  })}
                  linkLabel={t("app.suggestions.banner.reviewCta")}
                  linkTo={SUGGESTIONS_PATH}
                  bg="blue.400"
                  borderColor="blue.300"
                  dismissLabel={t("app.common.dismiss")}
                  onDismiss={() =>
                    dismissBanner(SUGGESTION_BANNER_KEYS.shortcuts)
                  }
                />
              )}

            <Box flex={1} px={6} py={4} overflowX="hidden" minW={0}>
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to="/admin/dashboard" replace />}
                />
                {/* Catch all unknown admin routes and redirect to dashboard */}
                <Route
                  path="*"
                  element={<Navigate to="/admin/dashboard" replace />}
                />
              </Routes>
            </Box>

            <Footer />
          </Box>
        </Box>
      </Box>
    </OnboardingProvider>
  );
}
